import { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";

// Utils
import { DataContext } from "../../../utils/DataContext";
import useLanguage from "../../../utils/useLanguage";
import { formatDate } from "../../../utils/toDate";

// Assets
import previousIcon from "../../../assets/previous_icon.png";
import nextIcon from "../../../assets/next_icon.png";
import addIcon from "../../../assets/add_icon.png";

// Styles
import classes from "../../../components/Modal.module.css";

// Components
import SectionDivider from "../../../components/SectionDivider";
import AddAnotherButton from "../../../components/AddAnotherButton";
import ErrorBox from "../../../components/ErrorBox";
import Loader from "../../../components/Loader";
import CustomInput from "../../../components/CustomInput";
import Dropdown from "../../../components/Dropdown";
import CustomButton from "../../../components/CustomButton";
import InformationBox from "../../../components/InformationBox";
import AreYouSureModal from "../../../components/AreYouSureModal";

const EditNoCustomerInvoice = ({ invoice, setInvoice }) => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const containerDiv = useRef(null);

  const { error } = useContext(DataContext);

  const [loadingModal, setLoadingModal] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [containerToDelete, setContainerToDelete] = useState(null);

  const [page, setPage] = useState(1);

  const handleAddInput = () => {
    const newContainer = {
      // containerNumber: `${container ${invoice?.shippedContainer + 1}}`,
      amountForSupplier: "",
      expectedArrivalDate: new Date(),
      finalDestination: "",
      material: "",
      unitPrice: "",
      numberOfBoxes: "",
      measuringUnit: "",
      shipper: "",
      shippingDate: new Date(),
      subCompany: "",
      exitDate: new Date(),
      abstractAmountForCustomer: "",
      finalAmountForCustomer: "",
      dateOfEntry: new Date(),
      permitsReceivingDate: new Date(),
      notes: "",
    };

    setPage(invoice?.ShippedContainer?.length + 1);

    setInvoice((prev) => ({
      ...prev,
      ShippedContainer: [...prev?.ShippedContainer, newContainer], // Append new container
    }));

    setTimeout(() => {
      containerDiv.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleInputChange = (e, containerIndex = null, type) => {
    const { name, value } = e.target;

    setInvoice((prevState) => {
      if (containerIndex !== null) {
        const updatedContainers = prevState?.ShippedContainer.map(
          (container, index) => {
            return index === containerIndex
              ? {
                  ...container,
                  [name]: type === "date" ? new Date(value) : value,
                }
              : container;
          }
        );

        return { ...prevState, ShippedContainer: updatedContainers };
      }

      // Otherwise, update the top-level state field
      return { ...prevState, [name]: value };
    });
  };

  const handleNext = () => {
    if (page < invoice?.ShippedContainer?.length) setPage(page + 1);
    setTimeout(() => {
      containerDiv.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handlePrevious = () => {
    if (page > 1) setPage(page - 1);
    setTimeout(() => {
      containerDiv.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleInvoiceInputChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => ({ ...prev, [name]: value }));
  };

  const handleAmountChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleInvoiceDateChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => ({ ...prev, [name]: new Date(value) }));
  };

  const handleDeleteContainer = (containerIndex) => {
    setInvoice((prev) => ({
      ...prev,
      ShippedContainer: prev.ShippedContainer.filter(
        (_, index) => index !== containerIndex
      ),
    }));

    // Adjust page if needed
    if (page > 1 && containerIndex === page - 1) {
      setPage(page - 1);
    } else if (page > invoice.ShippedContainer.length - 1) {
      setPage(invoice.ShippedContainer.length - 1);
    }
  };

  const handleAttemptDelete = (containerIndex) => {
    setContainerToDelete(containerIndex);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (containerToDelete !== null) {
      handleDeleteContainer(containerToDelete);
      setShowDeleteConfirm(false);
      setContainerToDelete(null);
    }
  };

  return (
    <div className="u-padding--bottom_400">
      {loadingModal ? <Loader /> : null}
      {error && !loadingModal && (
        <div className="fit-content u-margin-center">
          <ErrorBox>{t(error)}</ErrorBox>
        </div>
      )}
      <div className={` add-shipped-container-invoice-part`} ref={containerDiv}>
        <SectionDivider title="invoiceDetails" />
        <div className={classes.inputContainer}>
          <span>{t("company")}: </span>
          <CustomInput
            name="subCompany"
            value={invoice?.subCompany?.nameInAr}
            onChange={() => null}
            flex
            readonly
            disabled
          />
        </div>
        <div className={classes.inputContainer}>
          <span>{t("invoiceNumber")}: </span>
          <div className={classes.invoiceNumberContainer}>
            <CustomInput
              name="invoiceNumber"
              value={invoice?.invoiceNumber}
              onChange={handleInvoiceInputChange}
              flex={true}
            />
          </div>
        </div>
        <div className={classes.inputContainer}>
          <span>{t("amountForSupplier")}: </span>
          <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
            <CustomInput
              name="amountForSupplier"
              value={invoice?.amountForSupplier}
              onChange={handleAmountChange}
              type="moneyV2"
              flex={true}
            />
            <div className="t-bold_bold">{invoice?.supplier?.currency}</div>
          </div>
        </div>
        <div className={classes.inputContainer}>
          <span>{t("Date")}: </span>
          <CustomInput
            name="date"
            type="date"
            flex={true}
            value={formatDate(invoice?.date)}
            onChange={(e) => handleInvoiceDateChange(e)}
          />
        </div>
        <div className={classes.inputContainer}>
          <span className={classes.notesClass}>{t("notes")}: </span>
          <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
            <CustomInput
              name="notes"
              value={invoice?.notes}
              onChange={handleInputChange}
              type="textarea"
              flex={true}
            />
          </div>
        </div>
      </div>
      {/* Containers */}
      <SectionDivider title="containersDetails" />
      {invoice?.ShippedContainer?.length ? (
        <div
          className={`add-shipped-container-container-part ${classes.flexableForm} ${classes.flexableFormWithPadding}`}
        >
          {invoice?.ShippedContainer?.map((item, index) => {
            if (index + 1 === page) {
              return (
                <>
                  <div className={classes.inputContainer}>
                    <span>{t("containerNumber")}: </span>
                    <div className={classes.invoiceNumberContainer}>
                      <CustomInput
                        name="containerNumber"
                        value={item?.containerNumber}
                        onChange={(e) => handleInputChange(e, page - 1)}
                        flex
                      />
                    </div>
                  </div>

                  {/* <div className={classes.inputContainer}>
                    <span>{t("amountForSupplier")}: </span>
                    <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
                      <CustomInput
                        name="amountForSupplier"
                        value={item?.amountForSupplier}
                        onChange={(e) => handleInputChange(e, page - 1)}
                        onKeyDown={(e) => onlyNums(e)}
                        type="money"
                        flex={true}
                      />
                      <div className="t-bold_bold">
                        {invoice?.supplier?.currency}
                      </div>
                    </div>
                  </div> */}

                  <div className={classes.inputContainer}>
                    <span>{t("expectedArrivalDate")}: </span>
                    <CustomInput
                      name="expectedArrivalDate"
                      type="date"
                      value={formatDate(item?.expectedArrivalDate)}
                      onChange={(e) => handleInputChange(e, page - 1, "date")}
                      flex
                    />
                  </div>

                  <div className={classes.inputContainer}>
                    <span>{t("finalDestination")}: </span>
                    <CustomInput
                      name="finalDestination"
                      value={item?.finalDestination}
                      onChange={(e) => handleInputChange(e, page - 1)}
                      flex={true}
                    />
                  </div>

                  <div className={classes.inputContainer}>
                    <span>{t("material")}: </span>
                    <CustomInput
                      name="material"
                      value={item?.material}
                      onChange={(e) => handleInputChange(e, page - 1)}
                      flex={true}
                    />
                  </div>

                  {/* <div className={classes.inputContainer}>
                    <span>{t("unitPrice")}: </span>
                    <div className="o-row u-width_full-width o-row--vertical-align_center o-row--gap-size_300">
                      <CustomInput
                        name="unitPrice"
                        value={item?.unitPrice}
                        onChange={(e) => handleInputChange(e, page - 1)}
                        flex={true}
                      />
                      <div className="t-bold_bold">
                        {invoice?.supplier?.currency}
                      </div>
                    </div>
                  </div> */}

                  <div className={classes.inputContainer}>
                    <span>{t("numberOfBoxes")}: </span>
                    <CustomInput
                      name="numberOfBoxes"
                      value={item?.numberOfBoxes}
                      onChange={(e) => handleInputChange(e, page - 1)}
                      type="moneyV2"
                      flex={true}
                    />
                  </div>

                  <div className={classes.inputContainer}>
                    <span>{t("measuringUnit")}: </span>
                    <CustomInput
                      name="measuringUnit"
                      value={item?.measuringUnit}
                      onChange={(e) => handleInputChange(e, page - 1)}
                      flex={true}
                    />
                  </div>

                  <div className={classes.inputContainer}>
                    <span>{t("shipper")}: </span>
                    <CustomInput
                      name="shipper"
                      value={item?.shipper}
                      onChange={(e) => handleInputChange(e, page - 1)}
                      flex={true}
                    />
                  </div>

                  {/* Date of Entry */}
                  <div className={classes.inputContainer}>
                    <span>{t("dateOfEntry")}: </span>
                    <CustomInput
                      name="dateOfEntry"
                      type="date"
                      flex={true}
                      value={formatDate(item?.dateOfEntry)}
                      onChange={(e) => handleInputChange(e, page - 1, "date")}
                    />
                  </div>

                  {/* Shipping Date */}
                  <div className={classes.inputContainer}>
                    <span>{t("shippingDate")}: </span>
                    <CustomInput
                      name="shippingDate"
                      type="date"
                      flex={true}
                      value={formatDate(item?.shippingDate)}
                      onChange={(e) => handleInputChange(e, page - 1, "date")}
                    />
                  </div>

                  {/* Exit Date */}
                  <div className={classes.inputContainer}>
                    <span>{t("exitDate")}: </span>
                    <CustomInput
                      name="exitDate"
                      type="date"
                      flex={true}
                      value={formatDate(item?.exitDate)}
                      onChange={(e) => handleInputChange(e, page - 1, "date")}
                    />
                  </div>

                  {/* Permits Receiving Date */}
                  <div className={classes.inputContainer}>
                    <span>{t("permitsReceivingDate")}: </span>
                    <CustomInput
                      name="permitsReceivingDate"
                      type="date"
                      flex={true}
                      value={formatDate(item?.permitsReceivingDate)}
                      onChange={(e) => handleInputChange(e, page - 1, "date")}
                    />
                  </div>

                  {/* Delivered or NOT delivered */}
                  <div className={classes.inputContainer}>
                    <span>{t("deliveryStatus")}: </span>
                    <Dropdown
                      options={[
                        { title: "delivered", value: true },
                        {
                          title: "notDelivered",
                          value: false,
                        },
                      ]}
                      selected={
                        item?.delivered
                          ? {
                              title: "delivered",
                              value: true,
                            }
                          : {
                              title: "notDelivered",
                              value: false,
                            }
                      }
                      setSelected={(status) => {
                        handleInputChange(
                          {
                            target: {
                              name: "delivered",
                              value: status?.value,
                            },
                          },
                          page - 1
                        );
                      }}
                      flex
                    />
                  </div>

                  {/* Notes */}
                  <div className={classes.inputContainer}>
                    <span className={classes.notesClass}>{t("notes")}: </span>
                    <CustomInput
                      name="notes"
                      type="textarea"
                      value={item?.notes || ""}
                      onChange={(e) => handleInputChange(e, page - 1)}
                      flex={true}
                    />
                  </div>
                  <div className="o-row o-row--horizontal-align_end">
                    <button
                      onClick={() => handleAttemptDelete(page - 1)}
                      className="temp-delete-button"
                      type="button"
                    >
                      ❌ {t("delete")}
                    </button>
                  </div>
                </>
              );
            }
            return null;
          })}
        </div>
      ) : (
        <InformationBox>{t("noContainers")}</InformationBox>
      )}
      <div>
        {invoice?.ShippedContainer?.length ? (
          <div className={"add-shipped-container-next-previous"}>
            <CustomButton
              variant="blank"
              title={t("previous")}
              onClick={handlePrevious}
              disabled={page === 1}
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={15}
            />
            <CustomButton
              title={t("next")}
              variant="blank"
              onClick={handleNext}
              disabled={page === invoice?.ShippedContainer?.length}
              icon={currentDirection === "ltr" ? nextIcon : previousIcon}
              iconSize={15}
              reverseIcon
            />
          </div>
        ) : null}
      </div>
      <div className={"add-shipped-container-add-another"}>
        <AddAnotherButton
          onClick={handleAddInput}
          variant="blank"
          title={t("addContainer")}
          icon={addIcon}
          iconSize={16}
          disabled={loadingModal}
          arrayLength={invoice?.ShippedContainer?.length}
        />
      </div>

      {showDeleteConfirm && (
        <AreYouSureModal
          customMessage={"confirmDeleteContainer"}
          closeTitle={"cancel"}
          confirmTitle={"delete"}
          onClose={() => {
            setShowDeleteConfirm(false);
            setContainerToDelete(null);
          }}
          onConfirm={() => handleConfirmDelete()}
          confirmIconType="whiteCheck"

        />
      )}
    </div>
  );
};

export default EditNoCustomerInvoice;
